import { fetchWithCSRFToken } from "@/services/rest";
import { store } from "@/state/store";
import { pushEvent } from "./dataLayerLogin";
import { $t } from "@/main";

export type LoginAction = "login" | "registration";
type ValidationCredentials = { j_username?: string };

const contextPath: string = window.ocm.config.encodedContextPath;
const validateUrls = {
	login: `${contextPath}/rest/auth/keycloak/login/`,
	registration: `${contextPath}/rest/auth/keycloak/registration/`,
};

export async function authenticate(
	validationCredentials: ValidationCredentials,
	action: LoginAction,
	csrfTokenAttempts = 0
): Promise<boolean> {
	resetErrors();

	const validateUrl = validateUrls[action];
	const bodyData = new URLSearchParams(validationCredentials).toString();

	const fetchOptions = {
		method: "post",
		headers: {
			"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
		},
		body: bodyData,
	};

	//+++++++++++++++++++++++++++++++++++++++++++++++++++++++++
	// make Request
	const responseData: ResponseData = await fetchWithCSRFToken(validateUrl, fetchOptions);
	const { httpStatus, globalErrorMessages, redirectUrl } = responseData;
	if (httpStatus === 200) {
		_handleOK(action, redirectUrl);
		return true;
	} else if (httpStatus === 412) {
		// _handle412 calls authenticate recursively and returns success as a Promise (true / false)
		return _handle412(action, validationCredentials, csrfTokenAttempts);
	} else if (httpStatus === 503) {
		handleNetworError();
	} else {
		const errorData: ErrorData = {
			errorMessages: globalErrorMessages || [$t("errorMessages.genericError")],
			httpStatus,
		};
		handleError(action, errorData);
	}

	return false;
}

function _handleOK(action: LoginAction, redirectUrl: string | undefined) {
	if (redirectUrl) {
		location.assign(redirectUrl);
	} else {
		pushEvent("backend_error", "missing_redirect_url");
		store.globalErrorMessages = [$t("errorMessages.genericError")];
	}
	if (action === "login") localStorage.setItem("loginTabActive", "true");
}

function _handle412(action: LoginAction, validationCredentials: ValidationCredentials, csrfTokenAttempts: number) {
	if (csrfTokenAttempts++ < 3) {
		// call authenticate recursively and return success as a Promise (true / false)
		return authenticate(validationCredentials, action, csrfTokenAttempts);
	}
	store.httpErrorStatusCode = 412;
	store.globalErrorMessages = [$t("errorMessages.httpErrorStatusCode412")];

	// ERROR TRACKING
	handleErrorTracking(action, 412);
	return false;
}

function handleNetworError() {
	pushEvent("backend_error", "invalid_authentication_response");
	store.globalErrorMessages = [$t("errorMessages.httpError")];
}

function handleError(action: LoginAction, errorData: ErrorData) {
	store.httpErrorStatusCode = errorData.httpStatus;
	store.globalErrorMessages = errorData.errorMessages;

	// EROOR TRACKING
	handleErrorTracking(action, errorData.httpStatus);
}

function handleErrorTracking(action: LoginAction, httpStatus: number) {
	const eventLabel = `${action}_error_status_${httpStatus.toString()}`;
	pushEvent("backend_error", eventLabel);
}

function resetErrors() {
	store.httpErrorStatusCode = null;
	store.globalErrorMessages = [];
}
